import { z } from 'zod';
import type {
    SupportedCredentialSystemId,
    RuntimeThirdPartyCredentials,
    ThirdPartyCredentials,
    ThirdPartyCredentialsDraft,
} from '../../secret.types';
import {
    CardPointeCredentialsSchema,
    DraftCardPointeCredentialsSchema,
    StoredCardPointeCredentialsParser,
    StoredCardPointeCredentialsPublicParser,
} from './cardpointe/cardpointe-credential.schemas';
import { CardPointeThirdPartySystemId } from '../../types/ipg/cardpointe/cardpointe-system-constants';
import {
    CloverPersistedOAuthCredentialsV2GrantBindingSchema,
    CloverPersistedOAuthCredentialsParser,
    CloverStoredOAuthGrantBindingToResolvedCredentialsSchema,
    CloverStoredOAuthCredentialsSchema,
    CloverStoredOAuthToResolvedCredentialsSchema,
    DraftCloverOAuthCredentialsSchema,
    DraftCloverOAuthGrantBindingCredentialsSchema,
    DraftCloverOAuthGrantBindingPublicCredentialsSchema,
    DraftCloverOAuthPublicCredentialsSchema,
} from './clover/clover-credential.schemas';
import { CloverThirdPartySystemId } from '../../types/ipg/clover/clover-system-constants';
import { CloverCredentialDefinitionKey } from '../../types/ipg/clover/clover-credential-definition-key';
import {
    DraftValorCredentialsSchema,
    ValorCredentialsSchema,
} from './valor/valor-credential.schemas';
import { ValorThirdPartySystemId } from '../../types/ipg/valor/valor-system-constants';

export const IpgThirdPartyCredentialsSchema: z.ZodType<ThirdPartyCredentials> = z.union([
    CardPointeCredentialsSchema,
    CloverStoredOAuthCredentialsSchema,
    ValorCredentialsSchema,
]);

export const IpgThirdPartyCredentialsDraftSchema: z.ZodType<ThirdPartyCredentialsDraft> = z.union([
    DraftCardPointeCredentialsSchema,
    DraftCloverOAuthCredentialsSchema,
    DraftValorCredentialsSchema,
]);

export const IpgCredentialsSchemaBySystemId: Partial<
    Record<SupportedCredentialSystemId, z.ZodType<ThirdPartyCredentials>>
> = {
    [CardPointeThirdPartySystemId]: CardPointeCredentialsSchema,
    [CloverThirdPartySystemId]: CloverStoredOAuthCredentialsSchema,
    [ValorThirdPartySystemId]: ValorCredentialsSchema,
};

export const IpgDraftCredentialsSchemaBySystemId: Partial<
    Record<SupportedCredentialSystemId, z.ZodType<ThirdPartyCredentialsDraft>>
> = {
    [CardPointeThirdPartySystemId]: DraftCardPointeCredentialsSchema,
    [CloverThirdPartySystemId]: DraftCloverOAuthCredentialsSchema,
    [ValorThirdPartySystemId]: DraftValorCredentialsSchema,
};

export const IpgCredentialsSchemaBySystemIdAndDefinitionKey: Partial<
    Record<SupportedCredentialSystemId, Readonly<Record<string, z.ZodType<ThirdPartyCredentials>>>>
> = {
    [CloverThirdPartySystemId]: {
        '': CloverStoredOAuthCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH]: CloverStoredOAuthCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH_GRANT_BINDING]:
            CloverPersistedOAuthCredentialsV2GrantBindingSchema,
    },
};

export const IpgDraftCredentialsSchemaBySystemIdAndDefinitionKey: Partial<
    Record<
        SupportedCredentialSystemId,
        Readonly<Record<string, z.ZodType<ThirdPartyCredentialsDraft>>>
    >
> = {
    [CloverThirdPartySystemId]: {
        '': DraftCloverOAuthCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH]: DraftCloverOAuthCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH_GRANT_BINDING]:
            DraftCloverOAuthGrantBindingCredentialsSchema,
    },
};

export const IpgStoredDraftCredentialsSchemaBySystemIdAndDefinitionKey: Partial<
    Record<
        SupportedCredentialSystemId,
        Readonly<Record<string, z.ZodType<ThirdPartyCredentialsDraft>>>
    >
> = {
    [CardPointeThirdPartySystemId]: {
        '': StoredCardPointeCredentialsParser,
    },
    [CloverThirdPartySystemId]: {
        '': CloverPersistedOAuthCredentialsParser,
        [CloverCredentialDefinitionKey.OAUTH]: CloverPersistedOAuthCredentialsParser,
        [CloverCredentialDefinitionKey.OAUTH_GRANT_BINDING]:
            CloverPersistedOAuthCredentialsV2GrantBindingSchema.transform((credentials) => ({
                merchantId: credentials.merchantId,
            })),
    },
};

export const IpgPublicCredentialsSchemaBySystemIdAndDefinitionKey: Partial<
    Record<
        SupportedCredentialSystemId,
        Readonly<Record<string, z.ZodType<ThirdPartyCredentialsDraft>>>
    >
> = {
    [CardPointeThirdPartySystemId]: {
        '': StoredCardPointeCredentialsPublicParser,
    },
    [CloverThirdPartySystemId]: {
        '': DraftCloverOAuthPublicCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH]: DraftCloverOAuthPublicCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH_GRANT_BINDING]:
            DraftCloverOAuthGrantBindingPublicCredentialsSchema,
    },
};

export const IpgRuntimeCredentialsSchemaBySystemIdAndDefinitionKey: Partial<
    Record<
        SupportedCredentialSystemId,
        Readonly<Record<string, z.ZodType<RuntimeThirdPartyCredentials>>>
    >
> = {
    [CloverThirdPartySystemId]: {
        '': CloverStoredOAuthToResolvedCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH]: CloverStoredOAuthToResolvedCredentialsSchema,
        [CloverCredentialDefinitionKey.OAUTH_GRANT_BINDING]:
            CloverStoredOAuthGrantBindingToResolvedCredentialsSchema,
    },
};

export const IpgRuntimeCredentialsSchemaBySystemId: Partial<
    Record<SupportedCredentialSystemId, z.ZodType<RuntimeThirdPartyCredentials>>
> = {
    [CardPointeThirdPartySystemId]: StoredCardPointeCredentialsParser,
    [ValorThirdPartySystemId]: ValorCredentialsSchema,
};
